/**
 * The contact form — `docs/record-flyouts.md` §4.2, from
 * `24-record-flyouts/html/flyout-new-contact.html`.
 *
 * Grouped the way somebody reads a business card rather than the way the table
 * is laid out: who they are, how to reach them, and where they are. The three
 * phone numbers are genuinely three numbers — a mobile you text and an office
 * line you do not — and keeping them apart is the difference between a text
 * arriving and a receptionist taking a message.
 *
 * What changes from the `SlideOver` this replaces, beyond the 480 → 620 and
 * the kit:
 *
 * **The company is a `ComboInput`.** It was a `<select>` with an "Add new"
 * button that swapped the whole field for a text box and a second pair of
 * buttons — a form growing a smaller form inside itself. Now you type, and
 * the last row makes what is not there.
 *
 * **The duplicate warning no longer disables Save.** §1.7: a form that greys
 * out its own Save is refusing to say what is wrong. It says what is wrong,
 * offers to open the person you already have, and lets the server refuse the
 * write if you press on — which it does, and which is the real guard.
 */
import { useEffect, useMemo, useState } from 'react'
import { Sheet } from '../sheet.js'
import { Band, Button } from '../primitives.js'
import {
  AddressFields, ComboInput, Field, FormGrid, FormSection, PhoneInput, RecordLookup,
  SegmentedField, TextInput, Wide,
  type AddressValue, type ComboItem,
} from '../form/index.js'
import { request } from '../../lib/api.js'
import { IconCheck, IconCompany } from '../../frame/icons.js'

/** A property, as the lookup reads it. */
interface PropertyOption { id: string; name: string; sub?: string }

export interface Company {
  id: string
  name: string
  contactCount: number
}

/** Everything the form edits, as the API names it. */
export interface ContactFields {
  firstName: string
  lastName: string
  title: string
  email: string
  phone: string
  mobilePhone: string
  homePhone: string
  officePhone: string
  addressLine1: string
  addressLine2: string
  city: string
  state: string
  postalCode: string
  accountId: string
  accountName: string
  preferredChannel: string
  contactHours: string
}

const EMPTY: ContactFields = {
  firstName: '', lastName: '', title: '', email: '', phone: '', mobilePhone: '',
  homePhone: '', officePhone: '', addressLine1: '', addressLine2: '', city: '',
  state: '', postalCode: '', accountId: '', accountName: '', preferredChannel: '',
  contactHours: '',
}

/**
 * What repeats when you add the next one.
 *
 * Contacts arrive in clusters — three people at the same property management
 * company, on the same street, with the same office line. Keeping the company
 * and the address is the difference between adding three people and typing
 * one address three times.
 */
const CARRIED = [
  'accountId', 'accountName', 'officePhone',
  'addressLine1', 'addressLine2', 'city', 'state', 'postalCode',
] as const

/**
 * What the profile hands over. Nulls from the API rather than the form's own
 * empty strings, because that is the shape the record actually has.
 */
export type ContactRecord = {
  [K in keyof ContactFields]?: string | null
} & { id: string; version: number }

/** What the duplicate check found while somebody was still typing. */
interface Duplicate {
  contactId: string
  name: string
  matchedOn: 'email' | 'phone'
}

export interface ContactFormProps {
  open: boolean
  onClose: () => void
  /** The branch a new company is filed under. */
  orgUnitId: string
  /** Present when editing; absent when adding. */
  existing?: ContactRecord | null
  /**
   * What to start the name as, when this was opened from a lookup somebody
   * had already typed into — "Rita Alvarez" fills both boxes, "Rita" fills
   * the first. See `splitName` below for why the last word is the surname.
   */
  initialName?: string
  /** A number or an address to start from — an unknown sender in the Inbox (Activity, Board 8 B). */
  initialPhone?: string
  initialEmail?: string
  /** Raised when this form was opened from another — see `SheetProps.layer`. */
  layer?: number
  onSaved: (contactId: string, again?: boolean) => void
  /**
   * Offered when the duplicate check finds somebody, so the answer to "Jordan
   * Alvarez already has that email" can be "open the one you already have".
   */
  onOpenExisting?: (contactId: string) => void
}

export function ContactForm({
  open, onClose, orgUnitId, existing, initialName, initialPhone, initialEmail, layer, onSaved, onOpenExisting,
}: ContactFormProps) {
  const [form, setForm] = useState<ContactFields>(EMPTY)
  const [saved, setSaved] = useState<ContactFields>(EMPTY)
  const [companies, setCompanies] = useState<Company[]>([])
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [duplicates, setDuplicates] = useState<Duplicate[]>([])
  const [carried, setCarried] = useState(false)
  const [property, setProperty] = useState<PropertyOption | null>(null)

  useEffect(() => {
    if (!open) return
    request<{ items: Company[] }>('/contacts/companies')
      /* `?? []`: a malformed answer is a bug elsewhere, but crashing the
         whole app over a dropdown's options is a worse one. */
      .then((result) => setCompanies(result.items ?? []))
      .catch(() => setCompanies([]))
  }, [open])

  useEffect(() => {
    if (!open) return
    const start = existing
      ? { ...EMPTY, ...clean(existing) }
      : {
        ...EMPTY, ...splitName(initialName),
        ...(initialPhone ? { mobilePhone: initialPhone } : {}),
        ...(initialEmail ? { email: initialEmail } : {}),
      }
    setForm(start)
    setSaved(start)
    setProblem(null)
    setErrors({})
    setDuplicates([])
    setCarried(false)
  }, [open, existing, initialName, initialPhone, initialEmail])

  const set = (changes: Partial<ContactFields>) => {
    setForm((current) => ({ ...current, ...changes }))
    setErrors((current) => {
      const next = { ...current }
      for (const key of Object.keys(changes)) delete next[key]
      return next
    })
  }

  /*
   * The duplicate check, while they type rather than on submit.
   *
   * §4.6's rule is "merge duplicates, never delete", and the cheapest merge is
   * the one that never happens: finding the existing Jordan Alvarez before a
   * second one is created turns a data-cleanup job into a click. Only when
   * adding — an email that matches the record you are editing is that record.
   */
  useEffect(() => {
    if (!open || existing) { setDuplicates([]); return undefined }
    const email = form.email.trim()
    const phone = form.mobilePhone.trim() || form.phone.trim()
    if (!email && !phone) { setDuplicates([]); return undefined }

    const timer = setTimeout(() => {
      request<{ matches: Duplicate[] }>('/contacts/duplicates', {
        method: 'POST',
        body: { email: email || null, phone: phone || null },
      })
        .then((result) => setDuplicates(result.matches))
        .catch(() => setDuplicates([]))
    }, 400)
    return () => clearTimeout(timer)
  }, [open, existing, form.email, form.mobilePhone, form.phone])

  const changes = useMemo(
    () => (Object.keys(form) as (keyof ContactFields)[])
      .filter((key) => form[key] !== saved[key]).length,
    [form, saved])

  function check(next: ContactFields): Record<string, string> {
    const found: Record<string, string> = {}
    if (!next.firstName.trim()) found.firstName = 'A contact needs a first name'
    if (!next.lastName.trim()) found.lastName = 'A contact needs a last name'
    return found
  }

  /** Make a company from what was typed, without leaving the form. */
  async function createCompany(name: string): Promise<ComboItem | null> {
    try {
      const { id } = await request<{ id: string }>('/contacts/companies', {
        method: 'POST',
        body: { name, orgUnitId },
      })
      setCompanies((current) => [...current, { id, name, contactCount: 0 }])
      return { id, name }
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : 'That company could not be added.')
      return null
    }
  }

  async function save(again: boolean) {
    const found = check(form)
    setErrors(found)
    if (Object.keys(found).length > 0) return

    setBusy(true)
    setProblem(null)
    try {
      const body = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        title: form.title.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || form.mobilePhone.trim() || null,
        mobilePhone: form.mobilePhone.trim() || null,
        homePhone: form.homePhone.trim() || null,
        officePhone: form.officePhone.trim() || null,
        addressLine1: form.addressLine1.trim() || null,
        addressLine2: form.addressLine2.trim() || null,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        postalCode: form.postalCode.trim() || null,
        accountId: form.accountId || null,
        preferredChannel: form.preferredChannel || null,
        contactHours: form.contactHours.trim() || null,
      }

      if (existing) {
        await request(`/contacts/${existing.id}`, {
          method: 'PATCH',
          body: { ...body, version: existing.version },
        })
        onSaved(existing.id)
        return
      }

      const created = await request<{ id: string }>('/contacts', { method: 'POST', body })

      /*
       * The property, linked straight away. If the link fails the contact
       * still exists and is still right, so this does not throw.
       */
      if (property) {
        await request(`/contacts/${created.id}/links`, {
          method: 'POST',
          body: { propertyId: property.id, isPrimary: true },
        }).catch(() => undefined)
      }

      if (again) {
        const next = { ...EMPTY } as ContactFields
        for (const key of CARRIED) next[key] = form[key]
        setForm(next)
        setSaved(next)
        setCarried(true)
        setDuplicates([])
        /* The next person at the same place is the common case. */
        onSaved(created.id, true)
        return
      }

      onSaved(created.id)
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : 'That could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  const name = `${form.firstName} ${form.lastName}`.trim()
  const address: AddressValue = {
    line1: form.addressLine1, line2: form.addressLine2,
    city: form.city, state: form.state, postalCode: form.postalCode,
  }
  const company = companies.find((one) => one.id === form.accountId)

  if (!open) return null

  return (
    <Sheet
      open
      layer={layer}
      onClose={onClose}
      kind="contact"
      title={existing ? (name || 'Contact') : 'New contact'}
      sub="Contacts"
      dirty={changes > 0}
      changeCount={changes}
      saveLabel={existing ? 'Save contact' : 'Add contact'}
      onSave={() => void save(false)}
      note={Object.keys(errors).length > 0
        ? (
          <span className="text-danger">
            {Object.keys(errors).length === 1
              ? 'One field needs attention'
              : `${Object.keys(errors).length} fields need attention`}
          </span>
        )
        : undefined}
      secondary={!existing
        ? (
          <Button className="h-[34px]" disabled={busy} onClick={() => void save(true)}>
            Save &amp; add another
          </Button>
        )
        : undefined}
      primary={(
        <Button
          variant="primary"
          className="h-[34px]"
          disabled={busy}
          onClick={() => void save(false)}
        >
          <IconCheck size={15} />
          {busy ? 'Saving…' : existing ? 'Save contact' : 'Add contact'}
        </Button>
      )}
    >
      {problem && <Band tone="amber">{problem}</Band>}

      {/*
        * Somebody with this email or number already exists.
        *
        * It says so and offers to open them, rather than refusing the button:
        * the server rejects a true duplicate anyway, and a greyed-out Save
        * with a warning beside it is two ways of saying the same thing, one
        * of which is a dead end.
        */}
      {duplicates.length > 0 && (
        <Band
          tone="amber"
          title={`${duplicates[0]!.name} already has that ${duplicates[0]!.matchedOn}.`}
        >
          {' '}Two records of one person is what merging exists to prevent.
          {onOpenExisting && (
            <>
              {' '}
              <button
                type="button"
                onClick={() => onOpenExisting(duplicates[0]!.contactId)}
                className="font-semibold underline underline-offset-2"
              >
                Open {duplicates[0]!.name}
              </button>
            </>
          )}
        </Band>
      )}

      <FormSection title="Who" />
      <FormGrid>
        <Field label="First name" required error={errors.firstName}>
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              describedBy={describedBy}
              invalid={invalid}
              value={form.firstName}
              placeholder="Maria"
              onChange={(event) => set({ firstName: event.target.value })}
            />
          )}
        </Field>
        <Field label="Last name" required error={errors.lastName}>
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              describedBy={describedBy}
              invalid={invalid}
              value={form.lastName}
              placeholder="Santos"
              onChange={(event) => set({ lastName: event.target.value })}
            />
          )}
        </Field>

        <Field
          label="Company"
          hint={carried && form.accountId
            ? 'kept from the last one'
            : company && company.contactCount > 0
              ? `${company.name} already has ${company.contactCount} `
                + `${company.contactCount === 1 ? 'person' : 'people'} on it`
              : 'one shared list, so two people typing the same company get the same one'}
        >
          {({ id, describedBy }) => (
            <ComboInput
              id={id}
              describedBy={describedBy}
              label="Company"
              thing="company"
              placeholder="Search companies…"
              value={form.accountId
                ? { id: form.accountId, name: company?.name ?? form.accountName }
                : null}
              onPick={(picked) => set({ accountId: picked.id, accountName: picked.name })}
              search={(query) => companies
                .filter((one) => one.name.toLowerCase().includes(query.trim().toLowerCase()))
                .slice(0, 8)
                .map((one) => ({
                  id: one.id,
                  name: one.name,
                  /* The building in the mockup's Company menu. */
                  lead: <IconCompany size={14} />,
                  sub: one.contactCount > 0
                    ? `${one.contactCount} ${one.contactCount === 1 ? 'person' : 'people'}`
                    : 'no people yet',
                }))}
              onCreate={createCompany}
            />
          )}
        </Field>

        <Field label="Title" optional>
          {({ id }) => (
            <TextInput
              id={id}
              value={form.title}
              placeholder="Facilities manager"
              onChange={(event) => set({ title: event.target.value })}
            />
          )}
        </Field>
      </FormGrid>

      <FormSection
        title="How to reach them"
        sub="the first number and email are the defaults for texts and proposals"
      />
      <FormGrid>
        <Field label="Mobile">
          {({ id }) => (
            <PhoneInput
              id={id}
              value={form.mobilePhone}
              placeholder="(717) 555-0177"
              suffix={form.mobilePhone.trim() ? 'texts go here' : undefined}
              onChange={(next) => set({ mobilePhone: next })}
            />
          )}
        </Field>
        <Field
          label="Office"
          hint={carried && form.officePhone ? 'kept from the last one' : undefined}
        >
          {({ id, describedBy }) => (
            <PhoneInput
              id={id}
              describedBy={describedBy}
              value={form.officePhone}
              placeholder="(717) 555-0100 x204"
              onChange={(next) => set({ officePhone: next })}
            />
          )}
        </Field>

        <Field label="Home" optional>
          {({ id }) => (
            <PhoneInput
              id={id}
              value={form.homePhone}
              onChange={(next) => set({ homePhone: next })}
            />
          )}
        </Field>
        <Field label="Best time" optional>
          {({ id }) => (
            <TextInput
              id={id}
              value={form.contactHours}
              placeholder="Weekdays before 3"
              onChange={(event) => set({ contactHours: event.target.value })}
            />
          )}
        </Field>

        <Wide>
          <Field label="Email" hint="proposals and invoices go here">
            {({ id, describedBy }) => (
              <TextInput
                id={id}
                describedBy={describedBy}
                type="email"
                value={form.email}
                placeholder="maria@keystonebuilders.com"
                onChange={(event) => set({ email: event.target.value })}
              />
            )}
          </Field>
        </Wide>

        <Wide>
          <Field group label="Prefers">
            {({ labelId, describedBy }) => (
              <SegmentedField
                labelledBy={labelId}
                describedBy={describedBy}
                label="Prefers"
                value={(form.preferredChannel || 'either') as 'email' | 'sms' | 'either'}
                onChange={(value) => set({ preferredChannel: value })}
                options={[
                  { value: 'sms' as const, label: 'Text' },
                  { value: 'email' as const, label: 'Email' },
                  { value: 'either' as const, label: 'Either' },
                ]}
              />
            )}
          </Field>
        </Wide>
      </FormGrid>

      <FormSection
        title="Where"
        sub="their own address, not the site's — the bill often goes to another town"
      />
      <AddressFields
        value={address}
        onChange={(next) => set({
          addressLine1: next.line1, addressLine2: next.line2,
          city: next.city, state: next.state, postalCode: next.postalCode,
        })}
      />

      {!existing && (
        <>
          <FormSection
            title="Where they are the contact"
            sub="one site now; roles and bill-to are the record's own card"
          />
          <Field
            label="Property"
            optional
            hint="linked as the primary contact — more can be added on the record"
          >
            {({ id, describedBy }) => (
              <RecordLookup
                id={id}
                describedBy={describedBy}
                kind="property"
                label="Property"
                placeholder="Search properties…"
                orgUnitId={orgUnitId}
                value={property}
                onPick={(picked) => setProperty(picked as PropertyOption)}
              />
            )}
          </Field>
        </>
      )}
    </Sheet>
  )
}

/** Nulls from the API become empty strings; an input has no other empty. */
/**
 * A name typed into a lookup, split across the two boxes this form has.
 *
 * The last word is the surname and everything before it is not, which gets
 * "Rita Alvarez" and "Rita M Alvarez" both right and "Maria del Carmen
 * Alvarez Ruiz" wrong — but wrong in the box somebody is looking straight at,
 * with the caret one Tab away, rather than wrong in a record they will find
 * next year filed under M.
 */
function splitName(typed?: string): Partial<ContactFields> {
  const words = (typed ?? '').trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return {}
  if (words.length === 1) return { firstName: words[0]! }
  return { firstName: words.slice(0, -1).join(' '), lastName: words[words.length - 1]! }
}

function clean(source: Partial<Record<keyof ContactFields, string | null>>): Partial<ContactFields> {
  return Object.fromEntries(
    Object.entries(source)
      .filter(([key]) => key in EMPTY)
      .map(([key, value]) => [key, value ?? '']),
  ) as Partial<ContactFields>
}
